import _jsx from "@babel/runtime/helpers/jsx";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { deprecate } from 'react-is-deprecated';
import { modalFocusHandler } from '../Modal/modalHelpers.js';
import Button from '../Button/index.js';
import Heading from '../Heading/index.js';
import CloseButton from '../CloseButton/index.js';

/**
 * Sliding sheet implementation that provides a modal slide up from the bottom of the viewport.
 * It surrounds provided children with a heading along with a close button and a primary CTA button, both with callback
 * handlers.
 *
 * This component is also used by `PopoverSheet` when rendered on xs breakpoints.
 */

const BottomSheet = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    show = false,
    onClick,
    // deprecated
    onHide = () => {},
    children,
    buttonLabel = 'Done',
    className,
    heading,
    renderServerSide = false,
    ...rest
  } = _ref;
  const intialShow = renderServerSide || show;
  const [showSheet, setShowSheet] = useState(intialShow);
  const [openAnimation, setOpenAnimation] = useState(intialShow);
  const [renderedInDom, setRenderedInDom] = useState(intialShow); // to render content serverside -- for SEO

  const localRef = useRef(null);

  // Use react-bootstrap's classing to disable scroll state
  // on body, so that base page isn't interactive while sheet is open
  const toggleScrollBlock = disableScroll => {
    if (disableScroll) {
      document?.body?.classList?.add('modal-open');
    } else {
      document?.body?.classList?.remove('modal-open');
    }
  };
  const handleHideClick = useCallback(() => {
    // handle deprecated onClick callback for onHide functionaliuty
    if (typeof onClick === 'function') {
      onClick();
    }
    onHide();
  }, [onClick, onHide]);
  const handleHide = () => {
    setOpenAnimation(false);
    setTimeout(() => {
      setShowSheet(false);
    }, 350); // Need time out for the smooth scroll close to work
  };
  useEffect(() => {
    if (show) {
      setRenderedInDom(false);
      setShowSheet(true);
      setOpenAnimation(true);
      toggleScrollBlock(true);
    } else if (!show && !renderedInDom) {
      handleHide();
      toggleScrollBlock(false);
    }
  }, [show, renderedInDom]);
  useEffect(() => {
    // Direct and trap focus in sheet element to prevent background interaction
    if (showSheet && localRef?.current) {
      localRef?.current?.querySelector('.close')?.focus();
      const {
        focusHandler,
        prevFocusableElement
      } = modalFocusHandler(localRef.current);
      document.addEventListener('keydown', focusHandler, false);
      return () => {
        document.removeEventListener('keydown', focusHandler, false);
        prevFocusableElement.focus();
      };
    }
    return () => {};
  }, [showSheet]);
  if (!showSheet) {
    return null;
  }
  return /*#__PURE__*/React.createElement("div", Object.assign({
    "data-cmp": "bottom-sheet",
    className: clsx(className, 'bottom-sheet-container modal-backdrop'),
    ref: localRef
  }, rest), /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: clsx('bottom-sheet-wrapper', {
      'open-slide': openAnimation,
      'close-slide': !openAnimation
    })
  }, /*#__PURE__*/_jsx("div", {
    className: "bottom-sheet-content display-flex justify-content-between margin-bottom-4 align-items-start"
  }, void 0, /*#__PURE__*/_jsx(Heading, {
    level: 4,
    className: "text-primary"
  }, void 0, heading), /*#__PURE__*/_jsx("div", {
    className: "margin-top-2 margin-left-2"
  }, void 0, /*#__PURE__*/_jsx(CloseButton, {
    onClick: handleHideClick
  }))), children, /*#__PURE__*/_jsx(Button, {
    block: true,
    bsStyle: "primary",
    onClick: handleHideClick,
    className: "margin-top-5"
  }, void 0, buttonLabel)));
});
export default BottomSheet;